import { useState, useMemo } from 'react';
import { Check, ChevronsUpDown, Plus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useIngredients } from '@/hooks/useIngredients';
import { useCreateIngredient } from '@/hooks/useCreateIngredient';
import { useToast } from '@/hooks/use-toast';

const UNIT_OPTIONS = [
  { value: 'oz', label: 'Ounces (oz)' },
  { value: 'lb', label: 'Pounds (lb)' },
  { value: 'g', label: 'Grams (g)' },
  { value: 'kg', label: 'Kilograms (kg)' },
  { value: 'each', label: 'Each' },
  { value: 'cup', label: 'Cup' },
  { value: 'tbsp', label: 'Tablespoon' },
  { value: 'tsp', label: 'Teaspoon' },
  { value: 'ml', label: 'Milliliters (ml)' },
  { value: 'l', label: 'Liters (l)' },
];

const CATEGORY_OPTIONS_FOR_DIALOG = [
  { value: 'Food Cost', label: 'Food Cost' },
  { value: 'Packaging', label: 'Packaging' },
  { value: 'Supplies', label: 'Supplies' },
  { value: 'Non-Business', label: 'Non-Business' },
];

interface IngredientSearchComboboxProps {
  value?: string;
  onValueChange: (ingredientId: string | null, ingredientName: string, metadata?: { category?: string; standardUnit?: string; costPerStandardUnit?: number; purchaseUnit?: string }) => void;
  onCreateNew?: (ingredientName: string) => void;
  placeholder?: string;
  className?: string;
  category?: string;
  standardUnit?: string;
}

export function IngredientSearchCombobox({
  value,
  onValueChange,
  onCreateNew,
  placeholder = 'Search or create ingredient...',
  className,
  category,
  standardUnit,
}: IngredientSearchComboboxProps) {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newIngredientName, setNewIngredientName] = useState('');
  const [purchaseQuantity, setPurchaseQuantity] = useState('');
  const [newUnit, setNewUnit] = useState('');
  const [newCategory, setNewCategory] = useState('');
  const { toast } = useToast();
  const { data: ingredients = [], isLoading: loading } = useIngredients();
  const createIngredient = useCreateIngredient();
  
  // Map ingredients to the format expected by this component
  const mappedIngredients = useMemo(() => {
    return ingredients.map(ing => ({
      id: ing.id,
      name: ing.name,
      category: ing.category || 'Food Cost',
      cost_per_standard_unit: ing.cost_per_standard_unit,
      standard_unit: ing.standard_unit,
      purchase_unit: ing.purchase_unit,
    }));
  }, [ingredients]);

  const selectedIngredient = mappedIngredients.find((ing) => ing.id === value);
  const displayValue = selectedIngredient?.name || inputValue || '';

  const filteredIngredients = useMemo(() => {
    let filtered = mappedIngredients;
    if (category) {
      const categoryLower = category.toLowerCase().trim();
      filtered = mappedIngredients.filter(ing => {
        const ingCategoryLower = (ing.category || 'food cost').toLowerCase().trim();
        if (categoryLower === 'food cost' || categoryLower === 'food' || categoryLower === 'beverage cost' || categoryLower === 'beverage' || categoryLower === 'drinks') {
          return ingCategoryLower === 'food cost' || ingCategoryLower === 'beverage cost' || ingCategoryLower === 'beverage';
        }
        return ingCategoryLower === categoryLower;
      });
    }
    // When no category filter, show all ingredients
    
    // Then filter by search input if provided
    if (!inputValue) return filtered;
    const searchLower = inputValue.toLowerCase();
    
    // Separate prefix matches from substring matches for better UX
    const prefixMatches = filtered.filter(ing => 
      ing.name.toLowerCase().startsWith(searchLower)
    );
    const substringMatches = filtered.filter(ing => 
      !ing.name.toLowerCase().startsWith(searchLower) &&
      (ing.name.toLowerCase().includes(searchLower) ||
       ing.category.toLowerCase().includes(searchLower))
    );
    
    // Return prefix matches first, then substring matches
    return [...prefixMatches, ...substringMatches];
  }, [mappedIngredients, inputValue, category]);

  const handleSelect = (ingredient: typeof mappedIngredients[0]) => {
    onValueChange(ingredient.id, ingredient.name, {
      category: ingredient.category,
      standardUnit: ingredient.standard_unit,
      costPerStandardUnit: ingredient.cost_per_standard_unit,
      purchaseUnit: ingredient.purchase_unit,
    });
    setInputValue('');
    setOpen(false);
  };

  const handleCreateNew = async () => {
    const ingredientName = inputValue.trim();
    if (!ingredientName || createIngredient.isPending) return;
    
    // Open dialog to optionally add purchase quantity
    setNewIngredientName(ingredientName);
    setNewCategory(category || '');
    setShowCreateDialog(true);
    setOpen(false);
  };

  const handleConfirmCreate = async () => {
    const ingredientName = newIngredientName.trim();
    if (!ingredientName || createIngredient.isPending) return;
    
    try {
      // If onCreateNew is provided, use it (parent handles creation)
      // This maintains backward compatibility but parent should ideally use the mutation hook
      if (onCreateNew) {
        await onCreateNew(ingredientName);
        // Note: Cache will be invalidated by the mutation hook if parent uses it
        // Otherwise, we rely on the parent to handle cache invalidation
        setInputValue('');
        setShowCreateDialog(false);
        setPurchaseQuantity('');
        return;
      }
    
      const unitToUse = standardUnit?.trim() || newUnit?.trim();
      if (!unitToUse) {
        toast({
          title: 'Unit required',
          description: 'Please select a recipe unit for this new ingredient.',
          variant: 'destructive',
        });
        return;
      }

      const purchaseQty = purchaseQuantity ? parseFloat(purchaseQuantity) : undefined;
      const newIngredient = await createIngredient.mutateAsync({
        name: ingredientName,
        category: newCategory || category || 'Food Cost',
        standard_unit: unitToUse,
        purchase_unit: unitToUse,
        purchase_quantity: purchaseQty,
      });

      onValueChange(newIngredient.id, newIngredient.name, {
        category: category || 'Food Cost',
        standardUnit: unitToUse,
      });
      setInputValue('');
      setShowCreateDialog(false);
      setPurchaseQuantity('');
      setNewUnit('');
      setNewCategory('');
    } catch (error) {
      onValueChange(null, ingredientName, {
        category: category || 'Food Cost',
        standardUnit: standardUnit?.trim() || newUnit?.trim() || undefined,
      });
      setInputValue('');
      setShowCreateDialog(false);
      setPurchaseQuantity('');
      setNewUnit('');
      setNewCategory('');
    }
  };

  const matchesExisting = mappedIngredients.some(
    (ing) => ing.name.toLowerCase() === inputValue.toLowerCase()
  );

  // Auto-create ingredient when popover closes if there's a valid input value
  const handleOpenChange = (newOpen: boolean) => {
    // When closing (newOpen = false) and there's new input that doesn't match existing
    if (!newOpen && inputValue.trim() && !matchesExisting) {
      // Check if input is different from current selection
      const isDifferentFromCurrent = !selectedIngredient || 
        selectedIngredient.name.toLowerCase() !== inputValue.trim().toLowerCase();
      
      if (isDifferentFromCurrent) {
        // Open dialog to create the ingredient with optional purchase quantity
        setNewIngredientName(inputValue.trim());
        setNewCategory(category || '');
        setShowCreateDialog(true);
        return;
      }
    }
    setOpen(newOpen);
  };

  return (
    <>
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={cn(
              'w-full justify-between font-normal',
              !value && 'text-muted-foreground',
              className
            )}
          >
            <span className="truncate">{displayValue || placeholder}</span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[400px] p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Search ingredients..."
              value={inputValue}
              onValueChange={setInputValue}
            />
            <CommandList>
              {loading ? (
                <CommandEmpty>Loading ingredients...</CommandEmpty>
              ) : filteredIngredients.length > 0 ? (
                <CommandGroup heading="Existing Ingredients">
                  {filteredIngredients.map((ingredient) => (
                    <CommandItem
                      key={ingredient.id}
                      value={ingredient.id}
                      onSelect={() => handleSelect(ingredient)}
                      className="cursor-pointer"
                    >
                      <Check
                        className={cn(
                          'mr-2 h-4 w-4',
                          value === ingredient.id ? 'opacity-100' : 'opacity-0'
                        )}
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span>{ingredient.name}</span>
                          <Badge variant="outline" className="text-xs bg-red-600 text-white border-red-600">
                            {(ingredient.category || 'Food Cost').replace(/\b\w/g, c => c.toUpperCase())}
                          </Badge>
                        </div>
                        {ingredient.cost_per_standard_unit && (
                          <div className="text-xs text-muted-foreground">
                            ${ingredient.cost_per_standard_unit.toFixed(2)} / {ingredient.standard_unit || 'unit'}
                          </div>
                        )}
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              ) : null}
              {inputValue.trim() && !matchesExisting && (
                <CommandGroup>
                  <CommandItem
                    value={`create-${inputValue}`}
                    onSelect={handleCreateNew}
                    className="cursor-pointer"
                    disabled={createIngredient.isPending}
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    <span>
                      {createIngredient.isPending ? 'Creating...' : `Create "${inputValue.trim()}"`}
                    </span>
                  </CommandItem>
                </CommandGroup>
              )}
              {!loading && filteredIngredients.length === 0 && !inputValue.trim() && (
                <CommandEmpty>No ingredients found. Start typing to create a new one.</CommandEmpty>
              )}
              {!loading && filteredIngredients.length === 0 && inputValue.trim() && matchesExisting && (
                <CommandEmpty>No ingredients found matching "{inputValue}".</CommandEmpty>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Ingredient</DialogTitle>
            <DialogDescription>
              Add a new ingredient to your inventory. You can optionally add purchase quantity now or later during COGS training.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="ingredient-name">Ingredient Name</Label>
              <Input
                id="ingredient-name"
                value={newIngredientName}
                onChange={(e) => setNewIngredientName(e.target.value)}
                placeholder="e.g., Tomatoes"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-ingredient-category">Category *</Label>
              <Select value={newCategory} onValueChange={setNewCategory}>
                <SelectTrigger id="new-ingredient-category">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORY_OPTIONS_FOR_DIALOG.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {!standardUnit && (
              <div className="space-y-2">
                <Label htmlFor="new-ingredient-unit">Recipe Unit *</Label>
                <Select value={newUnit} onValueChange={setNewUnit}>
                  <SelectTrigger id="new-ingredient-unit">
                    <SelectValue placeholder="Select unit" />
                  </SelectTrigger>
                  <SelectContent>
                    {UNIT_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="purchase-quantity">
                Purchase Quantity <span className="text-muted-foreground text-xs">(Optional)</span>
              </Label>
              <Input
                id="purchase-quantity"
                type="number"
                step="0.01"
                value={purchaseQuantity}
                onChange={(e) => setPurchaseQuantity(e.target.value)}
                placeholder="e.g., 25"
              />
              <p className="text-xs text-muted-foreground">
                How many {standardUnit || newUnit || 'units'} you typically purchase at once
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowCreateDialog(false);
                setPurchaseQuantity('');
                setNewCategory('');
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmCreate}
              disabled={
                !newIngredientName.trim() ||
                !newCategory ||
                (!standardUnit && !newUnit) ||
                createIngredient.isPending
              }
            >
              {createIngredient.isPending ? 'Creating...' : 'Create Ingredient'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

